// Copyright 2026, Imprivata, Inc.  All rights reserved.

import {
  assignEndpointsToGroup,
  createEndpointGroup,
  deleteEndpointGroup,
  findEndpointGroups,
} from '@api/portalServices';
import {
  type EndpointGroup,
  type EndpointGroupCreateResponse,
  EndpointGroupSource,
} from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError, isSessionError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ASSIGN_CHUNK_SIZE, chunk } from '../utils/endpointGroups';

/** `groupId: null` removes the endpoints from their manual group. */
export type AssignRequest = { groupId: string | null; endpointUuids: readonly string[] };

export type AssignOutcome =
  | { kind: 'done'; changed: number }
  | { kind: 'failed'; changed: number; total: number; error: ApiError };

/**
 * Each call is atomic and carries at most 50 endpoints, so chunks run one after
 * another and stop at the first failure; `changed` counts the chunks that landed.
 * Failures resolve rather than throw so the caller always gets the partial count.
 */
const assignInChunks = async ({
  groupId,
  endpointUuids,
}: AssignRequest): Promise<AssignOutcome> => {
  let changed = 0;
  for (const endpointChunk of chunk(endpointUuids, ASSIGN_CHUNK_SIZE)) {
    try {
      await assignEndpointsToGroup({ groupId: groupId ?? '', endpointUuids: endpointChunk });
    } catch (error) {
      return { kind: 'failed', changed, total: endpointUuids.length, error: error as ApiError };
    }
    changed += endpointChunk.length;
  }
  return { kind: 'done', changed };
};

/**
 * Group failures are shown inline, but session errors still go to the global
 * handler so an expired session signs the admin out.
 */
export const useSessionErrorEscalation = () => {
  const { emitError } = useErrorContext();
  return (error: ApiError) => {
    if (isSessionError(error)) {
      emitError(new AppError(error));
    }
  };
};

/**
 * Group mutations without the groups query, for components that must not subscribe to it: a
 * new subscriber refetches a failed query, which can remount that subscriber in a loop.
 * Every mutation refreshes the groups (member counts), the endpoints list (`groupIds`) and the
 * workstation settings view, which lists the groups and their workstation counts.
 */
export const useEndpointGroupMutations = () => {
  const queryClient = useQueryClient();

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKey.ENDPOINT_GROUPS] }),
      queryClient.invalidateQueries({ queryKey: [QueryKey.ENDPOINTS] }),
      queryClient.invalidateQueries({ queryKey: [QueryKey.WORKSTATION_SETTINGS] }),
    ]);

  const create = useMutation<EndpointGroupCreateResponse, ApiError, string>({
    mutationFn: displayName => createEndpointGroup({ displayName }),
    onSuccess: refresh,
    retry: false,
  });

  const remove = useMutation<void, ApiError, string>({
    mutationFn: groupId => deleteEndpointGroup({ groupId }),
    // A failed delete can mean the group is already gone, so the list is read again either way.
    onSettled: refresh,
    retry: false,
  });

  const assign = useMutation<AssignOutcome, ApiError, AssignRequest>({
    mutationFn: assignInChunks,
    onSettled: refresh,
    retry: false,
  });

  return { create, remove, assign };
};

/** Manual endpoint groups for the Endpoints page, with their mutations. */
export const useEndpointGroups = () => {
  const { tenantId } = useSession();

  const groups = useQuery<EndpointGroup[], ApiError>({
    queryKey: [QueryKey.ENDPOINT_GROUPS, tenantId],
    queryFn: async () =>
      (await findEndpointGroups({ source: EndpointGroupSource.MANUAL })).endpointGroups,
    enabled: Boolean(tenantId),
    retry: false,
  });

  return { groups, ...useEndpointGroupMutations() };
};
